import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { AssetSelectorComponent } from '../../shared/design-system/asset-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  AssetMaster,
  CatalogOffering,
  CatalogService,
  CatalogWarehouse,
  CmmsBomPreview,
  InventorySerial,
  ServiceResource,
  WorkOrder
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-cmms-work-order-detail',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, FormsModule,
    ButtonModule, InputTextModule, SelectModule, AssetSelectorComponent,
    InputNumberModule, TextareaModule, TableModule
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('catalog.cmms.workOrders.createTitle') : t('catalog.cmms.workOrders.detailTitle')"
      [description]="t('catalog.cmms.workOrders.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !workOrder()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.cmms.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.cmms.workOrders.newRecord') : workOrder()!.workOrderNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.cmms.workOrders.createHint') : workOrder()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/cmms/work-orders']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            } @else if (canAssign()) {
              <button pButton type="button" [label]="t('catalog.service.action.assign')" [disabled]="acting()" (click)="assign()"></button>
            } @else if (canStart()) {
              <button pButton type="button" [label]="t('catalog.service.action.start')" [disabled]="acting()" (click)="start()"></button>
            } @else if (canComplete()) {
              <button pButton type="button" [label]="t('catalog.service.action.complete')" [disabled]="acting()" (click)="complete()"></button>
            }
          </div>
        </section>

        @if (isCreateMode()) {
          <section class="form-grid">
            <label><span>{{ t('catalog.cmms.workOrderNumber') }}</span><input pInputText [(ngModel)]="form.workOrderNumber" /></label>
            <label><span>{{ t('catalog.service.title') }}</span><input pInputText [(ngModel)]="form.title" /></label>
            <label><span>{{ t('catalog.assets.asset') }}</span><ain-asset-selector [(ngModel)]="form.assetId" /></label>
          </section>
        } @else {
          <section class="summary-grid">
            <article><strong>{{ t('catalog.cmms.workOrderType') }}</strong><span>{{ workOrder()!.workOrderType }}</span></article>
            <article><strong>{{ t('catalog.assets.asset') }}</strong><span>{{ assetLabel(workOrder()!.assetId) }}</span></article>
            <article><strong>{{ t('catalog.service.completionNotes') }}</strong><span>{{ workOrder()!.completionNotes || '-' }}</span></article>
          </section>

          @if (canAssign()) {
            <section class="form-grid">
              <label><span>{{ t('catalog.service.resource') }}</span>
                <p-select [options]="resourceOptions()" [(ngModel)]="assignResourceId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
            </section>
          }

          @if (canReserveParts() || canConsumeParts()) {
            <section class="inventory-panel">
              <ain-info-hint
                [titleKey]="'catalog.cmms.inventorySection.title'"
                [descriptionKey]="'catalog.cmms.inventorySection.description'"
                [bulletKeys]="['catalog.cmms.inventorySection.bullet1', 'catalog.cmms.inventorySection.bullet2']" />

          @if (canReserveParts()) {
            <section class="form-grid consume-panel">
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="inventoryOfferingOptions()" [(ngModel)]="reserveForm.offeringId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.warehouse') }}</span>
                <p-select [options]="warehouseOptions()" [(ngModel)]="reserveForm.warehouseId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.quantity') }}</span>
                <p-inputNumber [(ngModel)]="reserveForm.quantity" [min]="0.001" />
              </label>
              <label class="full">
                <button pButton type="button" [label]="t('catalog.cmms.spareReserve.action')" [disabled]="acting()" (click)="reserveParts()"></button>
              </label>
            </section>

            <section class="lines-panel">
              <h4>{{ t('catalog.cmms.spareReservations.title') }}</h4>
              <p-table [value]="workOrder()?.spareReservations ?? []">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.inventory.offeringId') }}</th>
                    <th>{{ t('catalog.inventory.warehouse') }}</th>
                    <th>{{ t('catalog.inventory.quantity') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ offeringLabel(row.offeringId) }}</td>
                    <td>{{ warehouseLabel(row.warehouseId) }}</td>
                    <td>{{ row.quantity }}</td>
                    <td>{{ row.status }}</td>
                    <td>
                      @if (row.status === 'Reserved') {
                        <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.outbound.release')" [disabled]="acting()" (click)="releaseParts(row.id)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="5">{{ t('catalog.cmms.spareReservations.empty') }}</td></tr>
                </ng-template>
              </p-table>
            </section>

            <section class="form-grid consume-panel">
              <h4 class="full">{{ t('catalog.cmms.bom.title') }}</h4>
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="inventoryOfferingOptions()" [(ngModel)]="bomForm.parentOfferingId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.quantity') }}</span>
                <p-inputNumber [(ngModel)]="bomForm.parentQuantity" [min]="1" />
              </label>
              <label class="full actions-inline">
                <button pButton type="button" severity="secondary" [label]="t('catalog.cmms.bom.preview')" [disabled]="acting()" (click)="previewBom()"></button>
                <button pButton type="button" [label]="t('catalog.cmms.bom.explode')" [disabled]="acting()" (click)="explodeBom()"></button>
              </label>
              @if (bomPreview()) {
                <ul class="full bom-preview">
                  @for (line of bomPreview()!.components; track line.componentOfferingId) {
                    <li>{{ offeringLabel(line.componentOfferingId) }} — {{ line.totalQuantity }}</li>
                  }
                </ul>
              }
            </section>
          }

          @if (canConsumeParts()) {
            <section class="form-grid consume-panel">
              <h4 class="full">{{ t('catalog.service.consumeParts.title') }}</h4>
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="inventoryOfferingOptions()" [(ngModel)]="consumeForm.offeringId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.warehouse') }}</span>
                <p-select [options]="warehouseOptions()" [(ngModel)]="consumeForm.warehouseId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.tracking.serialId') }}</span>
                <p-select [options]="consumeSerialOptions()" [(ngModel)]="consumeForm.serialId" optionLabel="label" optionValue="value" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.quantity') }}</span>
                <p-inputNumber [(ngModel)]="consumeForm.quantity" [min]="0.001" />
              </label>
              <label>
                <span>{{ t('catalog.service.consumeParts.reference') }}</span>
                <input pInputText type="text" [(ngModel)]="consumeForm.referenceNumber" />
              </label>
              <label>
                <span>{{ t('catalog.service.unitCost') }}</span>
                <p-inputNumber [(ngModel)]="consumeForm.unitCost" [min]="0" />
              </label>
              <label class="full">
                <button pButton type="button" [label]="t('catalog.service.action.consumeParts')" [disabled]="acting()" (click)="consumeParts()"></button>
              </label>
            </section>
          }

          </section>
          }

          <section class="lines-panel">
            <h4>{{ t('catalog.service.consumptions.title') }}</h4>
            <p-table [value]="workOrder()?.consumptions ?? []">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.tracking.serialId') }}</th>
                  <th>{{ t('catalog.inventory.quantity') }}</th>
                  <th>{{ t('catalog.service.consumeParts.reference') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ offeringLabel(row.offeringId) }}</td>
                  <td>{{ row.serialId || '-' }}</td>
                  <td>{{ row.quantity }}</td>
                  <td>{{ row.referenceNumber }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="4">{{ t('catalog.cmms.noConsumptions') }}</td></tr>
              </ng-template>
            </p-table>
          </section>

          @if (canComplete()) {
            <section class="form-grid">
              <label class="full">
                <span>{{ t('catalog.service.completionNotes') }}</span>
                <textarea pTextarea rows="3" [(ngModel)]="completeForm.completionNotes"></textarea>
              </label>
              <label>
                <span>{{ t('catalog.cmms.maintenanceExpense') }}</span>
                <p-inputNumber [(ngModel)]="completeForm.maintenanceExpenseAmount" [min]="0" />
              </label>
            </section>
          }

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card, .summary-grid, .form-grid { display: grid; gap: 12px; margin-bottom: 16px; }
    .header-card { grid-template-columns: 1fr auto; align-items: start; padding: 16px; border: 1px solid #e5e7eb; border-radius: 12px; }
    .actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
    .summary-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
    .summary-grid article { display: grid; gap: 4px; padding: 12px; background: #f8fafc; border-radius: 8px; }
    .form-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .consume-panel, .lines-panel, .inventory-panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
    .inventory-panel { display: grid; gap: 12px; }
    .consume-panel h4, .lines-panel h4 { margin: 0 0 10px; font-size: 15px; }
    .actions-inline { display: flex; gap: 8px; flex-wrap: wrap; }
    .bom-preview { margin: 0; padding-inline-start: 18px; }
    .error { color: #b91c1c; font-size: 13px; }
  `]
})
export class CmmsWorkOrderDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly acting = signal(false);
  readonly workOrder = signal<WorkOrder | null>(null);
  readonly assets = signal<AssetMaster[]>([]);
  readonly resources = signal<ServiceResource[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly serials = signal<InventorySerial[]>([]);
  readonly bomPreview = signal<CmmsBomPreview | null>(null);
  readonly error = signal('');

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly resourceOptions = computed(() =>
    this.resources().filter(r => r.isActive).map(r => ({ label: `${r.code} - ${r.name}`, value: r.id }))
  );
  readonly inventoryOfferingOptions = computed(() =>
    this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }))
  );
  readonly warehouseOptions = computed(() =>
    this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }))
  );
  readonly consumeSerialOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.serials().map(s => ({ label: s.serialNumber, value: s.id }))
  ]);

  form = { workOrderNumber: '', title: '', assetId: null as string | null };
  assignResourceId: string | null = null;
  reserveForm = { offeringId: '', warehouseId: '', quantity: 1 };
  bomForm = { parentOfferingId: '', parentQuantity: 1, warehouseId: '' };
  consumeForm = { offeringId: '', warehouseId: '', serialId: '', quantity: 1, referenceNumber: '', unitCost: 0 };
  completeForm = { completionNotes: '', maintenanceExpenseAmount: 0 as number | null };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => { if (!this.isCreateMode()) this.reloadWorkOrder(); }
    );
    this.service.listAssets().subscribe({ next: assets => this.assets.set(assets) });
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.service.listWarehouses().subscribe({ next: rows => {
      this.warehouses.set(rows);
      const first = rows[0];
      if (first) {
        this.reserveForm.warehouseId = first.id;
        this.consumeForm.warehouseId = first.id;
        this.bomForm.warehouseId = first.id;
      }
    }});
    this.service.listInventorySerials().subscribe({ next: rows => this.serials.set(rows) });
    this.service.listServiceResources().subscribe({
      next: resources => {
        this.resources.set(resources);
        const first = resources.find(r => r.isActive);
        if (first) this.assignResourceId = first.id;
      }
    });

    if (this.isCreateMode()) { this.loading.set(false); return; }

    this.reloadWorkOrder();
  }

  reloadWorkOrder(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) { this.loading.set(false); return; }

    this.loading.set(true);
    this.service.getWorkOrder(id).subscribe({
      next: wo => {
        this.workOrder.set(wo);
        if (!this.consumeForm.referenceNumber) this.consumeForm.referenceNumber = wo.workOrderNumber;
        this.loading.set(false);
      },
      error: () => { this.error.set(this.t('catalog.cmms.notFound')); this.loading.set(false); }
    });
  }

  assetLabel(assetId: string): string {
    const asset = this.assets().find(a => a.id === assetId);
    return asset ? `${asset.assetNumber} — ${asset.name}` : assetId;
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(o => o.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  warehouseLabel(warehouseId: string): string {
    const warehouse = this.warehouses().find(w => w.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : warehouseId;
  }

  canAssign(): boolean { return this.workOrder()?.status === 'Draft'; }
  canStart(): boolean { return this.workOrder()?.status === 'Assigned'; }
  canReserveParts(): boolean {
    const status = this.workOrder()?.status;
    return status === 'Assigned' || status === 'InProgress';
  }
  canConsumeParts(): boolean {
    const status = this.workOrder()?.status;
    return status === 'Assigned' || status === 'InProgress';
  }
  canComplete(): boolean { return this.workOrder()?.status === 'InProgress'; }

  save(): void {
    if (!this.form.assetId) return;
    this.saving.set(true);
    this.service.createWorkOrder({
      assetId: this.form.assetId,
      maintenancePlanId: null,
      workOrderNumber: this.form.workOrderNumber,
      title: this.form.title,
      workOrderType: 'Corrective',
      requiresPostApproval: false
    }).subscribe({
      next: created => { this.saving.set(false); void this.router.navigate(['/cmms/work-orders', created.id]); },
      error: () => { this.saving.set(false); this.error.set(this.t('catalog.cmms.createFailed')); }
    });
  }

  assign(): void {
    const wo = this.workOrder();
    if (!wo || !this.assignResourceId) return;
    this.acting.set(true);
    this.service.assignWorkOrder(wo.id, this.assignResourceId).subscribe({
      next: updated => { this.workOrder.set(updated); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.service.assignFailed')); }
    });
  }

  start(): void {
    const wo = this.workOrder();
    if (!wo) return;
    this.acting.set(true);
    this.service.startWorkOrder(wo.id).subscribe({
      next: updated => { this.workOrder.set(updated); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.service.startFailed')); }
    });
  }

  consumeParts(): void {
    const wo = this.workOrder();
    if (!wo || !this.consumeForm.offeringId || !this.consumeForm.warehouseId) {
      this.error.set(this.t('catalog.service.consumeRequired'));
      return;
    }

    this.acting.set(true);
    this.error.set('');
    this.service.consumeWorkOrderParts(wo.id, {
      offeringId: this.consumeForm.offeringId,
      warehouseId: this.consumeForm.warehouseId,
      lotId: null,
      serialId: this.consumeForm.serialId || null,
      quantity: this.consumeForm.quantity,
      referenceNumber: this.consumeForm.referenceNumber.trim() || wo.workOrderNumber,
      unitCost: this.consumeForm.unitCost
    }).subscribe({
      next: () => { this.reloadWorkOrder(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.service.consumeFailed')); }
    });
  }

  reserveParts(): void {
    const wo = this.workOrder();
    if (!wo || !this.reserveForm.offeringId || !this.reserveForm.warehouseId) return;
    this.acting.set(true);
    this.service.reserveWorkOrderParts(wo.id, {
      offeringId: this.reserveForm.offeringId,
      warehouseId: this.reserveForm.warehouseId,
      quantity: this.reserveForm.quantity
    }).subscribe({
      next: () => { this.reloadWorkOrder(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.cmms.spareReserve.failed')); }
    });
  }

  releaseParts(reservationId: string): void {
    const wo = this.workOrder();
    if (!wo) return;
    this.acting.set(true);
    this.service.releaseWorkOrderParts(wo.id, reservationId).subscribe({
      next: () => { this.reloadWorkOrder(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.cmms.spareReserve.releaseFailed')); }
    });
  }

  previewBom(): void {
    const wo = this.workOrder();
    if (!wo || !this.bomForm.parentOfferingId) return;
    this.service.previewWorkOrderBom(wo.id, this.bomForm.parentOfferingId, this.bomForm.parentQuantity).subscribe({
      next: preview => this.bomPreview.set(preview),
      error: () => this.bomPreview.set(null)
    });
  }

  explodeBom(): void {
    const wo = this.workOrder();
    if (!wo || !this.bomForm.parentOfferingId || !this.bomForm.warehouseId) return;
    this.acting.set(true);
    this.service.explodeWorkOrderBom(wo.id, {
      parentOfferingId: this.bomForm.parentOfferingId,
      warehouseId: this.bomForm.warehouseId,
      parentQuantity: this.bomForm.parentQuantity
    }).subscribe({
      next: () => { this.reloadWorkOrder(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.cmms.bom.explodeFailed')); }
    });
  }

  complete(): void {
    const wo = this.workOrder();
    if (!wo) return;
    this.acting.set(true);
    this.error.set('');
    this.service.completeWorkOrder(wo.id, {
      completionNotes: this.completeForm.completionNotes.trim() || 'Completed',
      maintenanceExpenseAmount: this.completeForm.maintenanceExpenseAmount
    }).subscribe({
      next: updated => { this.workOrder.set(updated); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.service.completeFailed')); }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
